
import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowRight, Clock, Calendar, User } from 'lucide-react';
import { useTutorialMetadata } from '@/hooks/useMetadata';
import { getTutorialById } from '@/utils/sample-data';

export default function TutorialDetailPage() {
  const { tutorialId } = useParams<{ tutorialId: string }>();
  const { data: tutorial, isLoading, error } = useTutorialMetadata(tutorialId);
  
  // Fall back to static data if there's an error
  const fallbackTutorial = !tutorial ? getTutorialById(tutorialId || '') : null;
  const currentTutorial = tutorial || fallbackTutorial;

  // Update document metadata for SEO
  useEffect(() => {
    if (currentTutorial) {
      // Update page title
      document.title = `${currentTutorial.title} | Mate4Tech Knowledge Hub`;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', currentTutorial.description);
      
      // Update Open Graph tags
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', currentTutorial.title);
      
      let ogDesc = document.querySelector('meta[property="og:description"]');
      if (!ogDesc) {
        ogDesc = document.createElement('meta');
        ogDesc.setAttribute('property', 'og:description');
        document.head.appendChild(ogDesc);
      }
      ogDesc.setAttribute('content', currentTutorial.description);
      
      let ogImage = document.querySelector('meta[property="og:image"]');
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      ogImage.setAttribute('content', currentTutorial.featuredImage);
      
      // Add canonical URL
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute(
        'href', 
        `https://knowledge.mate4tech.com.au/tutorials/${tutorialId}`
      );
    }
    
    return () => {
      // Reset title when component unmounts
      document.title = 'Mate4Tech Knowledge Hub';
    };
  }, [currentTutorial, tutorialId]);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="container py-16 px-4 text-center">
          <div className="animate-pulse">Loading tutorial...</div>
        </div>
      </MainLayout>
    );
  }

  if (error || !currentTutorial) {
    return (
      <MainLayout>
        <div className="container py-16 px-4 text-center">
          <h1 className="text-3xl font-bold mb-4">Tutorial Not Found</h1>
          <p className="mb-8">The tutorial you're looking for doesn't exist.</p>
          <Link to="/tutorials">
            <Button>Browse All Tutorials</Button>
          </Link>
        </div>
      </MainLayout>
    );
  }

  const totalReadingTime = currentTutorial.chapters.reduce(
    (sum, chapter) => sum + chapter.readingTime, 0
  );

  return (
    <MainLayout showSidebar>
      <div className="container py-16 px-4">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Link to="/tutorials" className="hover:text-primary">Tutorials</Link>
            <span>/</span>
            <span>{currentTutorial.title}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-6">{currentTutorial.title}</h1>
          
          {/* Add structured data for SEO */}
          <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Course",
              "name": currentTutorial.title,
              "description": currentTutorial.description,
              "provider": {
                "@type": "Organization",
                "name": "Mate4Tech",
                "sameAs": "https://knowledge.mate4tech.com.au"
              },
              "hasCourseInstance": {
                "@type": "CourseInstance",
                "courseMode": "online",
                "instructor": {
                  "@type": "Person",
                  "name": currentTutorial.author
                }
              },
              "educationalLevel": currentTutorial.difficulty,
              "about": currentTutorial.tags.map(tag => ({ "@type": "Thing", "name": tag }))
            })
          }} />
          
          <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground mb-8">
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              <span>{currentTutorial.author}</span>
            </div>
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>Last updated {new Date(currentTutorial.lastUpdated).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{totalReadingTime} min read</span>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-8">
            {currentTutorial.tags.map(tag => (
              <Badge key={tag} variant="secondary">
                {tag}
              </Badge>
            ))}
          </div>
          
          <p className="text-lg">{currentTutorial.description}</p>
        </div>
        
        <div className="relative mb-8">
          <img 
            src={currentTutorial.featuredImage}
            alt={currentTutorial.title}
            className="w-full rounded-lg max-h-64 object-cover"
          />
        </div>
        
        <h2 className="text-2xl font-semibold mb-6">Chapters</h2>
        <div className="space-y-4">
          {currentTutorial.chapters.map((chapter, index) => (
            <Card key={chapter.slug} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-medium">
                    <span className="text-primary mr-2">{index + 1}.</span>
                    {chapter.title}
                  </h3>
                  <div className="text-sm text-muted-foreground mt-1">
                    {chapter.readingTime} min read
                  </div>
                </div>
                <Link to={`/tutorials/${currentTutorial.id}/chapters/${chapter.slug}`}>
                  <Button variant="ghost" size="sm">
                    Read Chapter
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </MainLayout>
  );
}
